import { escapeCsvCell } from 'src/admin/LAB/PoolManagement/csvEscape';
import type { InventoryRow } from 'src/admin/models/PoolInventoryModel';

/**
 * Column order for the pool export, mirroring the on-screen inventory
 * table (see `InventoryTable.svelte`) so the download matches what the
 * user sees. This is intentionally wider than {@link INVENTORY_CSV_HEADER}:
 * the export is a snapshot/report, not a re-upload source, so it carries
 * read-only columns (borrow status, prices, revenue) the parser ignores.
 */
const POOL_EXPORT_CSV_HEADER =
    'Symbol,Firm Borrow Status,Quantity Used,Current Quantity,Current Price,Fair Value,Current LAB Price,Daily Revenue';

/** The inventory-row fields the pool export serializes, in table order. */
type PoolExportRow = Pick<
    InventoryRow,
    | 'symbol'
    | 'borrowStatus'
    | 'quantityUsed'
    | 'currentQuantity'
    | 'currentPrice'
    | 'fairValuePrice'
    | 'currentLabPrice'
    | 'dailyRevenue'
>;

/**
 * Serialize the current pool inventory to CSV, one column per visible
 * table column and in the same order (see {@link POOL_EXPORT_CSV_HEADER}).
 * Nullable columns (borrow status, current/fair-value prices, quantity
 * used, daily revenue) serialize to an empty cell — the table renders
 * these as an em dash, but a blank is the machine-readable equivalent.
 * Cells are escaped per RFC 4180 for `,` and `"`; embedded CR/LF are
 * normalized to spaces by {@link escapeCsvCell}.
 */
export function createInventoryCsv(rows: ReadonlyArray<PoolExportRow>): string {
    const lines = [POOL_EXPORT_CSV_HEADER];
    for (const row of rows) {
        lines.push(
            [
                row.symbol,
                row.borrowStatus ?? '',
                row.quantityUsed ?? '',
                row.currentQuantity,
                row.currentPrice ?? '',
                row.fairValuePrice ?? '',
                row.currentLabPrice,
                row.dailyRevenue ?? '',
            ]
                .map(escapeCsvCell)
                .join(','),
        );
    }
    return lines.join('\n') + '\n';
}
